/**
 * Audit Service
 * 
 * Service for logging audit events for compliance and security purposes.
 * Tracks sensitive data access, user actions, and system events.
 * 
 * API Endpoints (to be implemented on server):
 * - POST /api/audit/log          - Log a single event
 * - POST /api/audit/log/batch    - Log multiple events
 * - GET  /api/audit/logs         - Query audit logs (admin only)
 */

import { logger } from '@/app/utils/logger';
import { api } from './apiClient';
import { auth } from '@/app/config/firebase';
import type {
  AuditActionType,
  AuditCategory,
  AuditSeverity,
  AuditLogRequest,
  AuditLogResponse,
  AuditBatchLogRequest,
  AuditBatchLogResponse,
  AuditQueryParams,
  AuditQueryResponse,
} from '@/app/types/audit';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const packageJson = require('../../../package.json');

// ─────────────────────────────────────────────────────────────
// Service Interface
// ─────────────────────────────────────────────────────────────

export interface IAuditService {
  /**
   * Log a single audit event
   */
  log(request: AuditLogRequest): Promise<AuditLogResponse>;
  
  /**
   * Log multiple audit events in batch
   */
  logBatch(request: AuditBatchLogRequest): Promise<AuditBatchLogResponse>;
  
  /**
   * Query audit logs (admin only)
   */
  queryLogs(params: AuditQueryParams): Promise<AuditQueryResponse>;
  
  // ─────────────────────────────────────────────────────────────
  // Convenience Methods for Common Actions
  // ─────────────────────────────────────────────────────────────
  
  /**
   * Log sensitive field access
   */
  logSensitiveFieldAccess(
    action: 'reveal_requested' | 'reveal_confirmed' | 'revealed' | 'hidden',
    fieldName: string,
    sensitivityLevel: string,
    resourceType: string,
    resourceId?: string,
    metadata?: Record<string, unknown>
  ): Promise<AuditLogResponse>;
  
  /**
   * Log record view
   */
  logRecordView(
    resourceType: string,
    resourceId: string,
    metadata?: Record<string, unknown>
  ): Promise<AuditLogResponse>;
  
  /**
   * Log record modification
   */
  logRecordModification(
    action: 'created' | 'updated' | 'deleted',
    resourceType: string,
    resourceId: string,
    metadata?: Record<string, unknown>
  ): Promise<AuditLogResponse>;
  
  /**
   * Log data export
   */
  logDataExport(
    resourceType: string,
    exportFormat: string,
    recordCount: number,
    metadata?: Record<string, unknown>
  ): Promise<AuditLogResponse>;
  
  /**
   * Log authentication events
   */
  logLogin(method: 'google' | 'email_password' | 'sso'): Promise<AuditLogResponse>;
  logLogout(): Promise<AuditLogResponse>;
  logSessionExpired(): Promise<AuditLogResponse>;
  logAccessDenied(
    attemptedAction: string,
    requiredPermission: string,
    resourceType?: string,
    resourceId?: string,
    pageUrl?: string
  ): Promise<AuditLogResponse>;
  
  /**
   * Log record search
   */
  logRecordSearch(
    resourceType: string,
    filtersApplied: Record<string, unknown>,
    resultCount: number,
    pageUrl?: string
  ): Promise<AuditLogResponse>;
}

// ─────────────────────────────────────────────────────────────
// Mock Implementation (Development)
// ─────────────────────────────────────────────────────────────

/**
 * Get app info including version from package.json
 * Uses Firebase auth.currentUser to get session timestamp
 */
function getAppInfo() {
  // Get current user from Firebase Auth
  const currentUser = auth.currentUser;
  
  // Use the user's metadata.lastSignInTime as session identifier
  // This is when the user last signed in (their current session)
  let sessionId: string | null = null;
  
  if (currentUser?.metadata?.lastSignInTime) {
    // Convert to Unix timestamp for consistency
    const lastSignIn = new Date(currentUser.metadata.lastSignInTime).getTime();
    sessionId = `session-${Math.floor(lastSignIn / 1000)}`;
  }
  
  return {
    app_name: 'rewards-admin',
    app_version: packageJson.version as string,
    session_id: sessionId,
  };
}

// Static fallback for mock service
const APP_INFO = {
  app_name: 'rewards-admin',
  app_version: packageJson.version as string,
};

// In-memory store for development
const auditLogStore: Array<{
  audit_id: string;
  request: AuditLogRequest;
  timestamp: string;
}> = [];

let auditIdCounter = 1;

function generateAuditId(): string {
  return `audit-${Date.now()}-${auditIdCounter++}`;
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

class MockAuditService implements IAuditService {
  async log(request: AuditLogRequest): Promise<AuditLogResponse> {
    // Simulate network delay
    await delay(50);
    
    const audit_id = generateAuditId();
    const timestamp = new Date().toISOString();
    
    // Store in memory
    auditLogStore.push({ audit_id, request, timestamp });
    
    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      logger.debug('[AUDIT SERVICE]', {
        audit_id,
        timestamp,
        ...request,
        client: APP_INFO,
      });
    }
    
    return {
      success: true,
      audit_id,
      timestamp,
    };
  }
  
  async logBatch(request: AuditBatchLogRequest): Promise<AuditBatchLogResponse> {
    await delay(100);
    
    const audit_ids: string[] = [];
    const timestamp = new Date().toISOString();
    
    for (const event of request.events) {
      const audit_id = generateAuditId();
      auditLogStore.push({ audit_id, request: event, timestamp });
      audit_ids.push(audit_id);
    }
    
    if (process.env.NODE_ENV === 'development') {
      logger.debug('[AUDIT SERVICE] Batch logged', { count: audit_ids.length });
    }
    
    return {
      success: true,
      logged_count: audit_ids.length,
      audit_ids,
    };
  }
  
  async queryLogs(params: AuditQueryParams): Promise<AuditQueryResponse> {
    await delay(100);
    
    let logs = [...auditLogStore];
    
    // Apply filters
    if (params.action) {
      logs = logs.filter(l => l.request.action === params.action);
    }
    if (params.category) {
      logs = logs.filter(l => l.request.category === params.category);
    }
    if (params.resource_type) {
      logs = logs.filter(l => l.request.resource.resource_type === params.resource_type);
    }
    if (params.resource_id) {
      logs = logs.filter(l => l.request.resource.resource_id === params.resource_id);
    }
    if (params.field_name) {
      logs = logs.filter(l => l.request.resource.field_name === params.field_name);
    }
    
    // Pagination
    const page = params.page ?? 1;
    const limit = params.limit ?? 50;
    const start = (page - 1) * limit;
    const paginatedLogs = logs.slice(start, start + limit);
    
    return {
      logs: paginatedLogs.map(l => ({
        audit_id: l.audit_id,
        timestamp: l.timestamp,
        action: l.request.action,
        category: l.request.category,
        severity: l.request.severity,
        user: {
          user_id: 'current-user', // Would come from auth context
          email: 'admin@example.com',
          role: 'admin',
        },
        resource: l.request.resource,
        context: l.request.context ?? {},
        client: {
          ...APP_INFO,
          user_agent: navigator.userAgent,
        },
      })),
      total: logs.length,
      page,
      limit,
      has_more: start + limit < logs.length,
    };
  }
  
  // ─────────────────────────────────────────────────────────────
  // Convenience Methods
  // ─────────────────────────────────────────────────────────────
  
  async logSensitiveFieldAccess(
    action: 'reveal_requested' | 'reveal_confirmed' | 'revealed' | 'hidden',
    fieldName: string,
    sensitivityLevel: string,
    resourceType: string,
    resourceId?: string,
    metadata?: Record<string, unknown>
  ): Promise<AuditLogResponse> {
    const actionType = `sensitive_field_${action}` as AuditActionType;
    
    return this.log({
      action: actionType,
      category: sensitivityLevel === 'phi' ? 'phi_access' : 'record_access',
      severity: action === 'revealed' ? 'critical' : 'warning',
      resource: {
        resource_type: resourceType,
        resource_id: resourceId,
        field_name: fieldName,
        sensitivity_level: sensitivityLevel,
      },
      context: {
        description: `User ${action} sensitive field: ${fieldName}`,
        metadata,
      },
    });
  }
  
  async logRecordView(
    resourceType: string,
    resourceId: string,
    metadata?: Record<string, unknown>
  ): Promise<AuditLogResponse> {
    return this.log({
      action: 'record_viewed',
      category: 'record_access',
      severity: 'info',
      resource: {
        resource_type: resourceType,
        resource_id: resourceId,
      },
      context: {
        description: `Admin viewed ${resourceType} record`,
        metadata,
      },
    });
  }
  
  async logRecordModification(
    action: 'created' | 'updated' | 'deleted',
    resourceType: string,
    resourceId: string,
    metadata?: Record<string, unknown>
  ): Promise<AuditLogResponse> {
    const actionType = `record_${action}` as AuditActionType;
    
    return this.log({
      action: actionType,
      category: 'data_modification',
      severity: action === 'deleted' ? 'critical' : 'warning',
      resource: {
        resource_type: resourceType,
        resource_id: resourceId,
      },
      context: {
        description: `User ${action} ${resourceType} record`,
        metadata,
      },
    });
  }
  
  async logDataExport(
    resourceType: string,
    exportFormat: string,
    recordCount: number,
    metadata?: Record<string, unknown>
  ): Promise<AuditLogResponse> {
    return this.log({
      action: 'record_exported',
      category: 'record_access',
      severity: 'critical',
      resource: {
        resource_type: resourceType,
      },
      context: {
        description: `Admin exported ${recordCount} ${resourceType} records as ${exportFormat}`,
        metadata: {
          ...metadata,
          export_format: exportFormat,
          record_count: recordCount,
        },
      },
    });
  }
  
  async logLogin(method: 'google' | 'email_password' | 'sso'): Promise<AuditLogResponse> {
    return this.log({
      action: 'login' as AuditActionType,
      category: 'authentication',
      severity: 'info',
      resource: { resource_type: 'admin_user' },
      context: {
        description: `Admin logged in via ${method}`,
        metadata: { login_method: method },
      },
    });
  }
  
  async logLogout(): Promise<AuditLogResponse> {
    return this.log({
      action: 'logout' as AuditActionType,
      category: 'authentication',
      severity: 'info',
      resource: { resource_type: 'admin_user' },
      context: { description: 'Admin logged out' },
    });
  }
  
  async logSessionExpired(): Promise<AuditLogResponse> {
    return this.log({
      action: 'session_expired' as AuditActionType,
      category: 'authentication',
      severity: 'warning',
      resource: { resource_type: 'admin_user' },
      context: { description: 'Admin session expired' },
    });
  }
  
  async logAccessDenied(
    attemptedAction: string,
    requiredPermission: string,
    resourceType?: string,
    resourceId?: string,
    pageUrl?: string
  ): Promise<AuditLogResponse> {
    return this.log({
      action: 'access_denied' as AuditActionType,
      category: 'authentication',
      severity: 'warning',
      resource: {
        resource_type: resourceType || 'unknown',
        resource_id: resourceId,
      },
      context: {
        description: `Access denied: ${attemptedAction}`,
        metadata: {
          attempted_action: attemptedAction,
          required_permission: requiredPermission,
          page_url: pageUrl,
        },
      },
    });
  }
  
  async logRecordSearch(
    resourceType: string,
    filtersApplied: Record<string, unknown>,
    resultCount: number,
    pageUrl?: string
  ): Promise<AuditLogResponse> {
    return this.log({
      action: 'record_searched' as AuditActionType,
      category: 'record_access',
      severity: 'info',
      resource: { resource_type: resourceType },
      context: {
        description: `Admin searched ${resourceType} records`,
        metadata: {
          filters_applied: filtersApplied,
          result_count: resultCount,
          page_url: pageUrl,
        },
      },
    });
  }
}

// ─────────────────────────────────────────────────────────────
// Real Implementation (Production)
// Connects to rewards-admin-service /api/v1/audit-logs endpoints
// ─────────────────────────────────────────────────────────────


/**
 * Transform frontend request to backend API format.
 * Backend uses different field names and structure.
 */
function transformToBackendRequest(request: AuditLogRequest) {
  return {
    event_type: request.action,
    category: request.category,
    severity: request.severity,
    resource: {
      resource_type: request.resource.resource_type,
      resource_id: request.resource.resource_id,
      field_name: request.resource.field_name,
      sensitivity_level: request.resource.sensitivity_level,
    },
    event_timestamp: new Date().toISOString(),
    description: request.context?.description,
    context: request.context?.metadata,
    client: getAppInfo(),
  };
}

/**
 * Transform backend response to frontend format.
 */
function transformFromBackendResponse(backendResponse: Record<string, unknown>): AuditLogResponse {
  return {
    success: true,
    audit_id: String(backendResponse.audit_id),
    timestamp: String(backendResponse.event_timestamp || backendResponse.created_at),
  };
}

class RealAuditService implements IAuditService {
  private endpoint: string = '/api/v1/audit-logs';
  
  async log(request: AuditLogRequest): Promise<AuditLogResponse> {
    try {
      const backendRequest = transformToBackendRequest(request);
      const response = await api.post(this.endpoint, backendRequest);
      return transformFromBackendResponse(response.data as Record<string, unknown>);
    } catch (error) {
      // Audit failures should NOT block UI operations
      const axiosError = error as { response?: { status?: number } };
      logger.warn('[AUDIT] Failed to log event', { 
        status: axiosError.response?.status,
        action: request.action,
      });
      
      return {
        success: false,
        audit_id: `failed-${Date.now()}`,
        timestamp: new Date().toISOString(),
      };
    }
  }
  
  async logBatch(request: AuditBatchLogRequest): Promise<AuditBatchLogResponse> {
    try {
      const backendRequest = {
        logs: request.events.map(transformToBackendRequest),
      };
      const response = await api.post(`${this.endpoint}/batch`, backendRequest);
      const data = response.data as Array<Record<string, unknown>>;
      
      return {
        success: true,
        logged_count: data.length,
        audit_ids: data.map((item) => String(item.audit_id)),
      };
    } catch (error) {
      const axiosError = error as { response?: { status?: number } };
      logger.warn('[AUDIT] Failed to log batch', { status: axiosError.response?.status });
      return {
        success: false,
        logged_count: 0,
        audit_ids: [],
      };
    }
  }
  
  async queryLogs(params: AuditQueryParams): Promise<AuditQueryResponse> {
    try {
      // Build query params object
      const queryParams: Record<string, string> = {};
      if (params.user_id) queryParams.actor_uid = params.user_id;
      if (params.action) queryParams.event_type = params.action;
      if (params.category) queryParams.category = params.category;
      if (params.resource_type) queryParams.resource_type = params.resource_type;
      if (params.resource_id) queryParams.resource_id = params.resource_id;
      if (params.field_name) queryParams.field_name = params.field_name;
      if (params.from_date) queryParams.from_date = params.from_date;
      if (params.to_date) queryParams.to_date = params.to_date;
      if (params.limit) queryParams.limit = String(params.limit);
      if (params.page) queryParams.offset = String((params.page - 1) * (params.limit ?? 50));
      
      const response = await api.get(this.endpoint, { params: queryParams });
      const data = response.data as Record<string, unknown>;
      const logs = data.logs as Array<Record<string, unknown>>;
      
      // Transform backend response to frontend format
      return {
        logs: logs.map((log) => {
          const resource = log.resource as Record<string, unknown> | undefined;
          return {
            audit_id: String(log.audit_id),
            timestamp: String(log.event_timestamp),
            action: log.event_type as AuditActionType,
            category: log.category as AuditCategory,
            severity: log.severity as AuditSeverity,
            user: {
              user_id: String((log.actor as Record<string, unknown>)?.uid ?? ''),
              email: String((log.actor as Record<string, unknown>)?.email ?? ''),
              role: String((log.actor as Record<string, unknown>)?.role ?? ''),
            },
            resource: {
              resource_type: String(resource?.resource_type ?? ''),
              resource_id: resource?.resource_id ? String(resource.resource_id) : undefined,
              field_name: resource?.field_name ? String(resource.field_name) : undefined,
              sensitivity_level: resource?.sensitivity_level ? String(resource.sensitivity_level) : undefined,
            },
            context: {
              description: log.description ? String(log.description) : undefined,
              metadata: log.context as Record<string, unknown> | undefined,
            },
            client: {
              app_name: APP_INFO.app_name,
              app_version: APP_INFO.app_version,
            },
          };
        }),
        total: data.total as number,
        page: params.page ?? 1,
        limit: data.limit as number,
        has_more: data.has_more as boolean,
      };
    } catch (error) {
      const axiosError = error as { response?: { status?: number } };
      logger.warn('[AUDIT] Failed to query logs', { status: axiosError.response?.status });
      return {
        logs: [],
        total: 0,
        page: params.page ?? 1,
        limit: params.limit ?? 50,
        has_more: false,
      };
    }
  }
  
  // Convenience methods delegate to main log method
  async logSensitiveFieldAccess(
    action: 'reveal_requested' | 'reveal_confirmed' | 'revealed' | 'hidden',
    fieldName: string,
    sensitivityLevel: string,
    resourceType: string,
    resourceId?: string,
    metadata?: Record<string, unknown>
  ): Promise<AuditLogResponse> {
    // Map action to backend event_type format
    const eventType = action === 'revealed' ? 'phi_accessed' 
      : action === 'hidden' ? 'phi_hidden'
      : action === 'reveal_confirmed' ? 'phi_accessed'
      : 'phi_accessed';
    
    return this.log({
      action: eventType as AuditActionType,
      category: sensitivityLevel === 'phi' ? 'phi_access' : 'record_access',
      severity: action === 'revealed' ? 'critical' : 'warning',
      resource: {
        resource_type: resourceType,
        resource_id: resourceId,
        field_name: fieldName,
        sensitivity_level: sensitivityLevel,
      },
      context: {
        description: `Admin ${action} sensitive field: ${fieldName}`,
        metadata: {
          ...metadata,
          action_detail: action,
        },
      },
    });
  }
  
  async logRecordView(
    resourceType: string,
    resourceId: string,
    metadata?: Record<string, unknown>
  ): Promise<AuditLogResponse> {
    return this.log({
      action: 'record_viewed' as AuditActionType,
      category: 'record_access',
      severity: 'info',
      resource: {
        resource_type: resourceType,
        resource_id: resourceId,
      },
      context: {
        description: `Admin viewed ${resourceType} record`,
        metadata,
      },
    });
  }
  
  async logRecordModification(
    action: 'created' | 'updated' | 'deleted',
    resourceType: string,
    resourceId: string,
    metadata?: Record<string, unknown>
  ): Promise<AuditLogResponse> {
    const eventType = `record_${action}` as AuditActionType;
    
    return this.log({
      action: eventType,
      category: 'data_modification',
      severity: action === 'deleted' ? 'critical' : 'warning',
      resource: {
        resource_type: resourceType,
        resource_id: resourceId,
      },
      context: {
        description: `Admin ${action} ${resourceType} record`,
        metadata,
      },
    });
  }
  
  async logDataExport(
    resourceType: string,
    exportFormat: string,
    recordCount: number,
    metadata?: Record<string, unknown>
  ): Promise<AuditLogResponse> {
    return this.log({
      action: 'record_exported' as AuditActionType,
      category: 'record_access',
      severity: 'critical',
      resource: {
        resource_type: resourceType,
      },
      context: {
        description: `Admin exported ${recordCount} ${resourceType} records as ${exportFormat}`,
        metadata: {
          ...metadata,
          export_format: exportFormat,
          record_count: recordCount,
        },
      },
    });
  }
  
  async logLogin(method: 'google' | 'email_password' | 'sso'): Promise<AuditLogResponse> {
    return this.log({
      action: 'login' as AuditActionType,
      category: 'authentication',
      severity: 'info',
      resource: { resource_type: 'admin_user' },
      context: {
        description: `Admin logged in via ${method}`,
        metadata: { login_method: method },
      },
    });
  }
  
  async logLogout(): Promise<AuditLogResponse> {
    return this.log({
      action: 'logout' as AuditActionType,
      category: 'authentication',
      severity: 'info',
      resource: { resource_type: 'admin_user' },
      context: { description: 'Admin logged out' },
    });
  }
  
  async logSessionExpired(): Promise<AuditLogResponse> {
    return this.log({
      action: 'session_expired' as AuditActionType,
      category: 'authentication',
      severity: 'warning',
      resource: { resource_type: 'admin_user' },
      context: { description: 'Admin session expired' },
    });
  }
  
  async logAccessDenied(
    attemptedAction: string,
    requiredPermission: string,
    resourceType?: string,
    resourceId?: string,
    pageUrl?: string
  ): Promise<AuditLogResponse> {
    return this.log({
      action: 'access_denied' as AuditActionType,
      category: 'authentication',
      severity: 'warning',
      resource: {
        resource_type: resourceType || 'unknown',
        resource_id: resourceId,
      },
      context: {
        description: `Access denied: ${attemptedAction}`,
        metadata: {
          attempted_action: attemptedAction,
          required_permission: requiredPermission,
          page_url: pageUrl,
        },
      },
    });
  }
  
  async logRecordSearch(
    resourceType: string,
    filtersApplied: Record<string, unknown>,
    resultCount: number,
    pageUrl?: string
  ): Promise<AuditLogResponse> {
    return this.log({
      action: 'record_searched' as AuditActionType,
      category: 'record_access',
      severity: 'info',
      resource: { resource_type: resourceType },
      context: {
        description: `Admin searched ${resourceType} records`,
        metadata: {
          filters_applied: filtersApplied,
          result_count: resultCount,
          page_url: pageUrl,
        },
      },
    });
  }
}

// ─────────────────────────────────────────────────────────────
// Service Export
// ─────────────────────────────────────────────────────────────

// Always use real service - audit logging must work in all environments
export const auditService: IAuditService = new RealAuditService();

// Export for testing/direct instantiation
export { MockAuditService, RealAuditService };

